/* NOTE: I need to call some actions in api slice before define them */

import {
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityId,
  EntityState,
  PayloadAction
} from '@reduxjs/toolkit';
import {
  getImagesFromMessages,
  groupMessagesByDate
} from 'contexts/MessagesContext/messageContext.settings';
import {
  MessageProps,
  MessagesTaskDetailsProps
} from 'contexts/MessagesContext/messagesContext.types';
import { Status } from 'enums/messages';
import type { RootState } from 'store';
import { selectPatient } from 'store/patients/patientsSlice';

import { selectChannels } from '../channels/channelsSlice';
import { chatApi } from './chatApi';

const messagesAdapter = createEntityAdapter<MessageProps, EntityId>({
  selectId: (message) => message._id,
  sortComparer: (a, b) => b.messageStatus.sent.localeCompare(a.messageStatus.sent)
});

const initialState: {
  chatTaskDetails: MessagesTaskDetailsProps | null;
  images: string[];
  loadingMessagesStatus: Status;
  messages: EntityState<MessageProps, EntityId>;
  nextKey: string | null;
  totalCount: number | null;
} = {
  chatTaskDetails: null,
  images: [],
  loadingMessagesStatus: Status.Idle,
  messages: messagesAdapter.getInitialState(),
  nextKey: null,
  totalCount: null
};

const chatSlice = createSlice({
  extraReducers: (builder) => {
    builder.addMatcher(
      chatApi.endpoints.getCareMessages.matchFulfilled,
      (state, { meta, payload }) => {
        // If the pageNo is 0, then we reset messages, to prevent mix messages from different patients
        if (!meta.arg.originalArgs.pageNo || meta.arg.originalArgs.pageNo === 0) {
          messagesAdapter.setMany(state.messages, payload.messages);
        } else {
          messagesAdapter.upsertMany(state.messages, payload.messages);
        }
        state.images.push(...getImagesFromMessages(payload.messages));
        state.totalCount = payload.totalCount;
        state.chatTaskDetails = payload.chatTaskDetails;
        state.loadingMessagesStatus = Status.Fulfilled;
      }
    );
    builder.addMatcher(chatApi.endpoints.getCareMessages.matchPending, (state) => {
      state.loadingMessagesStatus = Status.Pending;
    });
    builder.addMatcher(chatApi.endpoints.getCareMessages.matchRejected, (state) => {
      state.loadingMessagesStatus = Status.Rejected;
    });
  },
  initialState,
  name: 'chat',
  reducers: {
    addMessage: (state, action: PayloadAction<MessageProps>) => {
      if (action.payload?.fileName && action.payload?.filePath) {
        const newImage = getImagesFromMessages([action.payload]);
        state.images.push(...newImage);
      }
      messagesAdapter.upsertOne(state.messages, { ...action.payload });
      state.totalCount = messagesAdapter.getSelectors().selectTotal(state.messages);
    },
    clearMessages: () => initialState,
    markSeen: (state, action: PayloadAction<MessageProps>) => {
      const selected = messagesAdapter
        .getSelectors()
        .selectById(state.messages, action.payload._id);
      if (selected) {
        const update = {
          ...selected,
          receiverDetails:
            selected.receiverDetails?.map((details) => {
              const updatedDetails = action.payload.receiverDetails?.find(
                (message) => details.id === message.id
              );
              return {
                ...details,
                ...(updatedDetails || {})
              };
            }) || []
        };

        messagesAdapter.upsertOne(state.messages, update);
      }
    }
  }
});

export const { addMessage, clearMessages, markSeen } = chatSlice.actions;

const messagesSelectors = messagesAdapter.getSelectors<RootState>((state) => state.chat.messages);

export const selectMessages = createSelector(
  [messagesSelectors.selectAll, selectPatient, selectChannels],
  (messages, patient, channels) => {
    /**
     * @description
     * Remove messages that are not belong to particular patient.
     * It is possible to receive messages from previous patient because of race conditions.
     *
     * Filter the messages also by the channel id,
     * because we can have multiple channels within the same patient
     * and if switching between them, we can see messages from the other channel.
     */
    const filteredMessages = messages.filter((message) => {
      const currentPatientId = patient.patientInfo?._id || patient?.documents?.patientId;

      return (
        message.channelId === channels.currentChannel?.channelId &&
        (message.receiverDetails?.some((details) => details?.id === currentPatientId) ||
          message.senderDetails?.id === currentPatientId ||
          message.senderDetails?.userType?.shortCode === 'EX')
      );
    });

    return groupMessagesByDate(filteredMessages);
  }
);
export const selectChat = (state: RootState) => state.chat;

export const selectChatTaskDetails = (state: RootState) => state.chat.chatTaskDetails;

export default chatSlice.reducer;
